import { useState, useCallback, useEffect, useRef, type RefObject } from 'react';

const GAP = 16; // gap-4
const MORE_BUTTON_WIDTH = 80;

/**
 * Determines how many tabs fit in the container width.
 *
 * On mount (and when tabCount changes), all tabs render inside an overflow-hidden
 * container. The hook reads each child's offsetWidth, caches the widths, then
 * calculates the split using the cached values on every resize.
 */
const useOverflowTabs = (
  containerRef: RefObject<HTMLDivElement | null>,
  tabCount: number,
  pinnedCount: number
) => {
  const [visibleCount, setVisibleCount] = useState(tabCount);
  const widthCache = useRef<number[]>([]);
  const frameRef = useRef<number>(0);

  const calculate = useCallback(
    (availableWidth: number) => {
      const widths = widthCache.current;
      if (widths.length === 0) return;

      let used = 0;
      let fit = 0;

      for (let i = 0; i < widths.length; i++) {
        const gap = fit > 0 ? GAP : 0;
        const remaining = widths.length - i - 1;
        const reserve = remaining > 0 ? GAP + MORE_BUTTON_WIDTH : 0;

        if (used + gap + widths[i] + reserve <= availableWidth) {
          used += gap + widths[i];
          fit++;
        } else {
          break;
        }
      }

      setVisibleCount(Math.max(pinnedCount, fit));
    },
    [pinnedCount]
  );

  // Measure children and cache widths — runs when all tabs are in the DOM
  const measureAndCache = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const children = Array.from(container.children) as HTMLElement[];
    // Only cache when all tabs are actually rendered (initial paint or tab list changed)
    if (children.length >= tabCount) {
      widthCache.current = children.map((c) => c.offsetWidth);
    }

    calculate(container.offsetWidth);
  }, [containerRef, tabCount, calculate]);

  // When tab list changes, reset so all tabs render for measurement
  useEffect(() => {
    widthCache.current = [];
    setVisibleCount(tabCount);
  }, [tabCount]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onResize = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        if (widthCache.current.length === 0) {
          measureAndCache();
        } else {
          calculate(container.offsetWidth);
        }
      });
    };

    const observer = new ResizeObserver(onResize);
    observer.observe(container);

    // Initial measurement after paint
    frameRef.current = requestAnimationFrame(measureAndCache);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameRef.current);
    };
  }, [containerRef, measureAndCache, calculate]);

  return visibleCount;
};

export default useOverflowTabs;
